/**
 * GalleryDetail — one photo gallery at /gallery/:slug.
 *
 * Build-time lookup only: `cmsGalleryBySlug` is the snapshot written by
 * `npm run cms:sync`, and the prerender renders this page from that same
 * snapshot. No fetch, so the gallery body (including every thumbnail) is in
 * the static HTML a crawler receives.
 *
 * A slug that is not in the snapshot renders the site's normal NotFound
 * treatment rather than a bespoke error panel — the same markup (it is rendered
 * by the very same component, not copied) a reader sees for any unknown URL.
 */
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Container, PageHeader, Reveal } from '@/components/ui'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import NotFoundPage from '@/pages/NotFoundPage'
import { GalleryPhotos } from '@/components/gallery/PhotoGrid'
import { cmsGalleryBySlug } from '@/content/cms/galleries.generated'
import type { GalleryLocale } from '@/content/gallery-types'

/** Pick the locale-appropriate string; empty means "not filled in". */
function pickLocalized(
  field: Record<GalleryLocale, string>,
  locale: GalleryLocale,
): string {
  return (
    field[locale]?.trim() ||
    field['zh-Hant']?.trim() ||
    (locale === 'en' ? field['zh-Hans']?.trim() : '') ||
    field.en?.trim() ||
    field['zh-Hans']?.trim() ||
    ''
  )
}

function asGalleryLocale(language: string | undefined): GalleryLocale {
  if (language === 'zh-Hans' || language === 'en') return language
  return 'zh-Hant'
}

function formatEventDate(value: string | null, locale: GalleryLocale): string | null {
  if (!value) return null
  const time = Date.parse(value)
  if (!Number.isFinite(time)) return value
  return new Date(time).toLocaleDateString(locale === 'en' ? 'en-HK' : 'zh-HK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function GalleryDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const locale = asGalleryLocale(i18n.language)

  const gallery = slug ? cmsGalleryBySlug[slug] : undefined
  // Reuse the site's 404 page verbatim: same heading, same copy, same focus
  // styles. A gallery-specific error panel would be a second 404 design.
  if (!gallery) return <NotFoundPage />

  const title = pickLocalized(gallery.title, locale) || gallery.slug
  const description = pickLocalized(gallery.description, locale)
  const dateLabel = formatEventDate(gallery.event_date, locale)
  const photoUuids = gallery.photos.map((photo) => photo.media_uuid)
  const photos = gallery.photos.map((photo) => ({
    uuid: photo.media_uuid,
    caption: photo.caption,
  }))
  const layout = (gallery.display_mode ?? 'grid') as 'grid' | 'slider' | 'list'
  // The detail page's description is whatever the operator wrote; when it is
  // empty, fall back to a localised sentence so <meta name="description"> is
  // never blank (the SEO audit rejects an empty description).
  const seoDescription =
    description || t('seo.galleryDetailDescription', { title })

  return (
    <article aria-labelledby="gallery-detail-title">
      <SEO
        title={t('seo.galleryDetailTitle', { title })}
        description={seoDescription}
        keywords={t('seo.galleryKeywords')}
        jsonLd={buildBreadcrumbList([
          { name: t('nav.home'), path: '/' },
          { name: t('gallery.navLabel'), path: '/gallery' },
          { name: title, path: `/gallery/${gallery.slug}` },
        ])}
      />

      <PageHeader
        tag={t('gallery.navLabel')}
        title={title}
        description={description || undefined}
      />

      {/* Meta: the event date and the photo count. A gallery has no author or
          external link, so this is deliberately a short definition list. */}
      <section className="py-5 px-section-x bg-surface-2 border-b border-border">
        <Container size="lg">
          <Reveal>
            <dl className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              {dateLabel && (
                <div className="flex items-start gap-2">
                  <Icon
                    icon="lucide:calendar"
                    className="text-primary mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <div>
                    <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                      {t('gallery.eventDateLabel')}
                    </dt>
                    <dd className="text-ink font-semibold">
                      <time dateTime={gallery.event_date ?? undefined}>{dateLabel}</time>
                    </dd>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2">
                <Icon
                  icon="lucide:images"
                  className="text-primary mt-0.5 flex-shrink-0"
                  aria-hidden="true"
                />
                <div>
                  <dt className="text-ink-muted text-xs uppercase tracking-wide mb-0.5">
                    {t('gallery.photosLabel')}
                  </dt>
                  <dd className="text-ink font-semibold">
                    {t('gallery.photoCount', { count: photoUuids.length })}
                  </dd>
                </div>
              </div>
            </dl>
          </Reveal>
        </Container>
      </section>

      <section
        className="py-section-y px-section-x bg-surface"
        aria-labelledby="gallery-detail-title"
      >
        <Container size="lg">
          <h2 id="gallery-detail-title" className="sr-only">
            {title}
          </h2>

          {photos.length === 0 ? (
            <p className="text-ink-muted">{t('gallery.emptyPhotos')}</p>
          ) : (
            <Reveal>
              <GalleryPhotos photos={photos} layout={layout} />
            </Reveal>
          )}

          <Reveal delay={0.05}>
            <div className="mt-7">
              <Link
                to="/gallery"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:gap-3 transition-all focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                <Icon icon="lucide:arrow-left" aria-hidden="true" />
                {t('gallery.backToList')}
              </Link>
            </div>
          </Reveal>
        </Container>
      </section>
    </article>
  )
}
